import { PostCodeFilterInput } from "@the-volte/svc-core-sdk";
import { RequireOnlyOne } from "@the-volte/svc-core-sdk/lib/tsHelpers";
import gql from "graphql-tag";

import { channelSlug, productsPerPage } from "@app/constants";
import {
  basicProductFragment,
  pageInfo,
  productPricingFragment,
  shippingMethodsFragment,
} from "@graphql/fragments";
import { useTypedQuery } from "@graphql/queries";
import {
  convertSortByFromString,
  convertToAttributeScalar,
} from "@temp/core/utils";
import { IFilters } from "@types";

import {
  ProductList,
  ProductListVariables,
} from "./Product/gqlTypes/ProductList";

export const productList = gql`
  ${basicProductFragment}
  ${productPricingFragment}
  ${shippingMethodsFragment}
  ${pageInfo}
  query ProductList(
    $after: String
    $first: Int!
    $sortBy: ProductOrder
    $filter: ProductFilterInput
    $channel: String
    $queryType: String
    $filterIsb: Boolean
    $similarProductId: ID
  ) {
    products(
      after: $after
      first: $first
      sortBy: $sortBy
      filter: $filter
      channel: $channel
      public: true
      queryType: $queryType
      filterIsb: $filterIsb
      similarProductId: $similarProductId
    ) {
      edges {
        node {
          ...BasicProductFields
          ...ProductPricingFieldSF
          ...ShippingMethods
        }
      }
      totalCount
      pageInfo {
        ...PageInfo
      }
    }
  }
`;

export const useProductsQuery = (
  filters: IFilters,
  ids: RequireOnlyOne<{
    categoryId: string | undefined;
    collectionId: string | undefined;
  }>,
  after: string | undefined = undefined,
  postcodes: PostCodeFilterInput[] = [],
  lenderSlug = "",
  qType?: string
) => {
  const { categoryId, collectionId } = ids;
  const { categorySlugs } = filters;
  const variables: ProductListVariables = {
    filter: {
      prices: filters?.prices?.map(({ lte, gte }) => ({ lte, gte })),
      lenderRating: {
        gte: filters?.lenderRating?.sort((a, b) => a - b)?.[0] || null,
      },
      lenderSlug,
      collections:
        collectionId && qType !== "warehouse_sale" ? [collectionId] : undefined,
      categories: categoryId && !categorySlugs ? [categoryId] : undefined,
      channel: channelSlug,
      attributes: filters.attributes
        ? convertToAttributeScalar(filters.attributes)
        : {},
      postcodes: postcodes.length ? postcodes : filters.postcodes,
      availableDeliveryDate: filters.availableDeliveryDate,
      availableDeliveryPostcode: filters.availableDeliveryPostcode,
      search: filters?.search,
      categorySlugs,
    },
    channel: channelSlug,
    first: filters.pageSize || productsPerPage,
    after: after || undefined,
    sortBy: convertSortByFromString(filters?.sortBy || ""),
    public: true,
    queryType: qType,
    filterIsb: filters?.filterIsb || false,
    ...(filters?.similarProductId && {
      similarProductId: filters.similarProductId,
    }),
  };

  return useTypedQuery<ProductList, ProductListVariables>(productList, {
    variables,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    returnPartialData: true,
    skip: false, //! (categoryId || collectionId),
  });
};
